import { ILegalEntityResponseSchema } from '@monetize/types/app';
import { MBox, MFlex, MTag, MText } from '../../../components/Monetize';
import { createColumnHelper } from '../../../components/Monetize/DataTableNew/appTable';
import { actionCatalogColumn } from '../../../components/Monetize/DataTableNew/columnCatalog/structuralColumns';
import {
  ColumnCatalog,
  ColumnContextBase,
} from '../../../components/Monetize/DataTableNew/columnCatalog/types';
import {
  dateTimeBodyTemplate,
  getColHeaderFilters,
  getColHeaderWithSize,
  textBodyTemplate,
} from '../../../components/Monetize/DataTableNew/tableUtilsNew';
import { QUOTE_TEMPLATE_DISPLAY } from '../../../constants/quotes';
import { BLANK_TEMPLATE_VALUE } from '../../../hooks/useQuoteTemplatesList';
import { getAddress } from '../../../utils/address';
import { LegalEntityLogoCell } from './components/LegalEntityLogoCell';

const columnHelper = createColumnHelper<ILegalEntityResponseSchema>();

export type LegalEntityListColumnContext =
  ColumnContextBase<ILegalEntityResponseSchema>;

const TENANT_DEFAULT_TEMPLATE_LABEL = 'Tenant Default';

export function getQuoteTemplateDisplay(value?: string | null): string {
  if (!value || value === BLANK_TEMPLATE_VALUE) {
    return TENANT_DEFAULT_TEMPLATE_LABEL;
  }
  return QUOTE_TEMPLATE_DISPLAY[value] ?? value;
}

/**
 * tableId: local filter state (no FilterStateKeys)
 *
 * Header filters and sorting are only enabled where the legalEntities API
 * supports them (unknown sort/filter params return HTTP 400).
 */
export const LEGAL_ENTITY_LIST_CATALOG: ColumnCatalog<
  ILegalEntityResponseSchema,
  LegalEntityListColumnContext
> = [
  {
    id: 'logo',
    label: 'Logo',
    customizable: false,
    build: () =>
      columnHelper.display({
        id: 'logo',
        header: '',
        size: 72,
        cell: ({ row }) => (
          <LegalEntityLogoCell id={row.original.id} name={row.original.name} />
        ),
        enableSorting: false,
      }),
  },
  {
    id: 'name',
    label: 'Name',
    customizable: false,
    build: () =>
      columnHelper.accessor('name', {
        ...getColHeaderWithSize('Name', 250),
        ...getColHeaderFilters('TEXT'),
        cell: ({ row }) => {
          const { name, defaultEntity } = row.original;
          return (
            <MFlex alignItems="center" gap="2">
              <MText>{name}</MText>
              {defaultEntity && (
                <MTag size="sm" variant="blue">
                  Default
                </MTag>
              )}
            </MFlex>
          );
        },
        enableSorting: true,
      }),
  },
  {
    id: 'customId',
    label: 'Custom ID',
    defaultVisible: false,
    build: () =>
      columnHelper.accessor('customId', {
        ...getColHeaderWithSize('Custom ID', 200),
        ...getColHeaderFilters('TEXT'),
        cell: textBodyTemplate(),
        enableSorting: true,
      }),
  },
  {
    id: 'address',
    label: 'Address',
    build: () =>
      columnHelper.display({
        id: 'address',
        ...getColHeaderWithSize('Address', 260),
        cell: ({ row }) => {
          const { addressLine1, addressLine2, otherAddress } = getAddress(
            row.original,
            { addressFormat: row.original.addressFormat },
          );
          return (
            <MBox>
              {addressLine1 && <MText lineClamp={1}>{addressLine1}</MText>}
              {addressLine2 && <MText lineClamp={1}>{addressLine2}</MText>}
              {otherAddress && <MText lineClamp={1}>{otherAddress}</MText>}
            </MBox>
          );
        },
        enableSorting: false,
      }),
  },
  {
    id: 'email',
    label: 'Email',
    build: () =>
      columnHelper.accessor('email', {
        ...getColHeaderWithSize('Email', 220),
        ...getColHeaderFilters('TEXT'),
        cell: textBodyTemplate(),
        enableSorting: true,
      }),
  },
  {
    id: 'phoneNumber',
    label: 'Phone',
    build: () =>
      columnHelper.accessor('phoneNumber', {
        ...getColHeaderWithSize('Phone', 160),
        cell: textBodyTemplate(),
        enableSorting: false,
      }),
  },
  {
    id: 'vatNumber',
    label: 'VAT Number',
    build: () =>
      columnHelper.accessor('vatNumber', {
        ...getColHeaderWithSize('VAT Number', 160),
        cell: textBodyTemplate(),
        enableSorting: false,
      }),
  },
  {
    id: 'quoteTemplate',
    label: 'Quote Template',
    build: () =>
      columnHelper.accessor('quoteTemplate', {
        ...getColHeaderWithSize('Quote Template', 200),
        cell: ({ getValue }) => (
          <MText lineClamp={1}>{getQuoteTemplateDisplay(getValue())}</MText>
        ),
        enableSorting: false,
      }),
  },
  {
    id: 'modifyDate',
    label: 'Updated',
    build: () =>
      columnHelper.accessor('modifyDate', {
        header: 'Updated',
        cell: dateTimeBodyTemplate(),
        size: 180,
        enableSorting: true,
      }),
  },
  {
    id: 'createDate',
    label: 'Created',
    build: () =>
      columnHelper.accessor('createDate', {
        header: 'Created',
        cell: dateTimeBodyTemplate(),
        size: 180,
        enableSorting: true,
      }),
  },
  actionCatalogColumn<ILegalEntityResponseSchema>(),
];
